import clsx from 'clsx'
import { type MouseEvent, type ReactNode, type Ref, useCallback } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useScreenSizeOnly } from 'hooks/useScreenSize'

const InteractiveElements = 'a, button, input, select, textarea, label, [role="button"], [role="link"], [role="radio"], [role="checkbox"], [contenteditable="true"]'

const Tile = styled.div`
  display: grid;
  grid-template: "details" 1fr "pricing" auto "cta" auto / minmax(0, 1fr);
  min-width: 0;
  gap: ${rem(12)};
  padding: ${rem(16)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.M};
  box-shadow: ${props => props.theme.shadow.flat.small};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  &.has-banner {
    grid-template: "banner" auto "details" 1fr "pricing" auto "cta" auto / minmax(0, 1fr);
  }

  &.selected {
    border-color: ${props => props.theme.palette.highlight.primary.darkForeground};
    box-shadow: none;
  }

  ${mediaQueryUp.tablet} {
    &.selected {
      border-color: ${props => props.theme.palette.neutral.surface.lightForeground};
      box-shadow: ${props => props.theme.shadow.flat.small};
    }
  }

  &.error {
    border-color: ${props => props.theme.palette.messaging.critical.normalForeground};
  }

  ${mediaQueryUp.desktop} {
    grid-template: "details pricing cta" auto / minmax(0, 1fr) minmax(${rem(120)}, var(--package-selection-pricing-width, 30%)) ${rem(136)};
    &.has-banner {
      grid-template: "banner banner banner" auto "details pricing cta" auto / minmax(0, 1fr) minmax(${rem(120)}, var(--package-selection-pricing-width, 30%)) ${rem(136)};
    }
    border-radius: 0;
    &, &.selected { box-shadow: none; }
    border-width: 1px 0;
    border-color: var(--palette-neutral-surface-light-foreground, ${props => props.theme.palette.neutral.surface.lightForeground});
  }
`

const Banner = styled.div`
  grid-area: banner;
`

const Details = styled.div`
  grid-area: details;
  min-width: 0;
`

const Pricing = styled.div`
  grid-area: pricing;
  min-width: 0;
`

const Actions = styled.div`
  grid-area: cta;
  min-width: 0;
`

interface Props {
  /** Named regions own responsive placement; consumers must not depend on child order. */
  banner?: ReactNode
  details: ReactNode
  pricing: ReactNode
  actions: ReactNode
  selected?: boolean
  error?: boolean
  className?: string
  ref?: Ref<HTMLDivElement>
  'data-testid'?: string
  onSelect?: () => void
  selectionDisabled?: boolean
}

function AccommodationPackageSelectionTile({ selected, error, className, onSelect, selectionDisabled, banner, details, pricing, actions, ...props }: Props) {
  const isMobile = useScreenSizeOnly('mobile')
  const handleClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    if (!isMobile || selected || selectionDisabled || event.defaultPrevented) return
    const target = event.target
    if (!(target instanceof Element) || target.closest(InteractiveElements)) return
    onSelect?.()
  }, [isMobile, onSelect, selected, selectionDisabled])

  return <Tile
    {...props}
    onClick={onSelect ? handleClick : undefined}
    className={clsx(className, { selected, error, 'has-banner': !!banner })}
  >
    {banner && <Banner>{banner}</Banner>}
    <Details>{details}</Details>
    <Pricing>{pricing}</Pricing>
    <Actions>{actions}</Actions>
  </Tile>
}

export default AccommodationPackageSelectionTile
